import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner as BootstrapSpinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faSave, faTrash, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ConfirmationModal } from '../../../components/UnifiedModal';
import useUser from '../../../hooks/useUser';
import {
  createEnvVarSet,
  deleteEnvVarSet,
  listEnvVarSets,
  updateEnvVarSet,
} from '../../../utils/savedSecrets';
import { getErrorMessage } from '../../../utils/errorMessage';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  readThroughAccountSettingsCache,
} from '../accountSettingsDataCache';
import './SavedEnvVarSets.css';

interface EnvVarRow {
  key: string;
  value: string;
}

interface SavedEnvVarSet {
  id: string;
  displayName: string;
  keyNames?: string[];
}

interface EnvVarPayload {
  displayName: string;
  envVars?: Record<string, string>;
}

const emptyRow = (): EnvVarRow => ({ key: '', value: '' });

const rowsToEnvVars = (rows: EnvVarRow[]): Record<string, string> => rows.reduce<Record<string, string>>((acc, row) => {
  const key = row.key.trim();
  if (key && row.value) {
    acc[key] = row.value;
  }
  return acc;
}, {});

const primaryRow = (rows: EnvVarRow[]): EnvVarRow => rows[0] || emptyRow();

const variableLabel = (keyNames: string[] = []): string => {
  const keys = keyNames.filter(Boolean);
  if (keys.length === 0) {
    return 'No variable name listed';
  }
  if (keys.length === 1) {
    return `Variable: ${keys[0]}`;
  }
  return `Variables: ${keys.join(', ')}`;
};

const SavedEnvVarSets = () => {
  const { getApiToken, user } = useUser();
  const itemsCacheKey = makeAccountSettingsCacheKey(
    'saved-variables',
    getAccountSettingsUserCacheKey(user),
    process.env.REACT_APP_API_URL || 'api',
  );
  const cachedItems = readAccountSettingsCache<SavedEnvVarSet[]>(itemsCacheKey);
  const [items, setItems] = useState<SavedEnvVarSet[]>(() => cachedItems || []);
  const [loading, setLoading] = useState(() => !cachedItems);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [rows, setRows] = useState<EnvVarRow[]>([emptyRow()]);
  const [revealedRows, setRevealedRows] = useState<Record<number, boolean>>({});
  const [editingItem, setEditingItem] = useState<SavedEnvVarSet | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedEnvVarSet | null>(null);

  const isEditing = Boolean(editingItem);

  const loadItems = useCallback(async (options: { force?: boolean } = {}) => {
    const cached = readAccountSettingsCache<SavedEnvVarSet[]>(itemsCacheKey);
    if (cached && !options.force) {
      setItems(cached);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const nextItems = await readThroughAccountSettingsCache<SavedEnvVarSet[]>(itemsCacheKey, async () => {
        const token = await getApiToken();
        return await listEnvVarSets(token) as SavedEnvVarSet[];
      }, options);
      setItems(nextItems);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load saved environment variables'));
    } finally {
      setLoading(false);
    }
  }, [getApiToken, itemsCacheKey]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const currentRow = primaryRow(rows);
  const canSubmit = isEditing
    ? Boolean(displayName.trim() || currentRow.value)
    : Boolean(currentRow.key.trim() && currentRow.value);

  const resetForm = () => {
    setDisplayName('');
    setRows([emptyRow()]);
    setRevealedRows({});
    setEditingItem(null);
  };

  const updateRow = (index: number, field: keyof EnvVarRow, value: string): void => {
    setRows((current) => current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [field]: value } : row
    )));
  };

  const beginEdit = (item: SavedEnvVarSet): void => {
    setEditingItem(item);
    setDisplayName(item.displayName);
    setRows([{ key: item.keyNames?.[0] || '', value: '' }]);
    setRevealedRows({});
    setError(null);
  };

  const toggleRowReveal = (index: number): void => {
    setRevealedRows((current) => ({ ...current, [index]: !current[index] }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const envVars = rowsToEnvVars(rows);
    const keyNames = Object.keys(envVars);
    if (!isEditing && keyNames.length === 0) {
      setError('Add the variable name and value');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = await getApiToken();
      const fallbackName = keyNames[0] || editingItem?.keyNames?.[0] || 'Saved variable';
      const payload: EnvVarPayload = { displayName: displayName.trim() || fallbackName };
      if (Object.keys(envVars).length > 0) {
        payload.envVars = envVars;
      }
      if (isEditing) {
        await updateEnvVarSet(token, editingItem?.id, payload);
      } else {
        await createEnvVarSet(token, payload);
      }
      resetForm();
      await loadItems({ force: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save environment variables'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: SavedEnvVarSet): void => {
    setPendingDelete(item);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    try {
      setDeletingId(pendingDelete.id);
      setError(null);
      const token = await getApiToken();
      await deleteEnvVarSet(token, pendingDelete.id);
      setPendingDelete(null);
      await loadItems({ force: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete environment variables'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="saved-env-var-sets-settings">
      <div className="settings-subpage-header saved-env-var-sets-settings__header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Saved Variables</h2>
          <p className="settings-subpage-description">
            Store secrets for strategy runs.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <section className="saved-env-var-sets-settings__form-section">
        <Form onSubmit={handleSubmit}>
          {isEditing && (
            <div className="saved-env-var-sets-settings__form-header">
              <Button variant="outline" size="sm" type="button" onClick={resetForm}>
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </Button>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Nickname</Form.Label>
            <Form.Control
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Optional"
              maxLength={160}
            />
            <Form.Text className="saved-env-var-sets-settings__field-help">
              Values stay hidden after saving. Fill only values you want to replace.
            </Form.Text>
          </Form.Group>

          <div className="saved-env-var-sets-settings__row-headings" aria-hidden="true">
            <span>Variable name</span>
            <span>Value</span>
          </div>
          <div className="saved-env-var-sets-settings__rows">
            {rows.map((row, index) => (
              <div key={index} className="saved-env-var-sets-settings__row">
                <span className="saved-env-var-sets-settings__mobile-label">Variable name</span>
                <Form.Control
                  value={row.key}
                  onChange={(event) => updateRow(index, 'key', event.target.value.toUpperCase())}
                  placeholder="GEMINI_API_KEY"
                  aria-label="Environment variable name"
                  disabled={isEditing}
                />
                <span className="saved-env-var-sets-settings__mobile-label">Value</span>
                <div className="saved-env-var-sets-settings__secret-input">
                  <Form.Control
                    className="saved-env-var-sets-settings__secret-control"
                    value={row.value}
                    onChange={(event) => updateRow(index, 'value', event.target.value)}
                    placeholder={isEditing ? 'Leave blank unless replacing this value' : 'Secret value'}
                    type={revealedRows[index] ? 'text' : 'password'}
                    aria-label="Environment variable value"
                  />
                  <button
                    type="button"
                    className="saved-env-var-sets-settings__secret-toggle"
                    onClick={() => toggleRowReveal(index)}
                    aria-label={`${revealedRows[index] ? 'Hide' : 'Show'} environment variable value`}
                    aria-pressed={Boolean(revealedRows[index])}
                    title={`${revealedRows[index] ? 'Hide' : 'Show'} value`}
                  >
                    <FontAwesomeIcon icon={revealedRows[index] ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="saved-env-var-sets-settings__actions">
            <Button type="submit" disabled={saving || !canSubmit}>
              {saving ? <><BootstrapSpinner size="sm" animation="border" /> Saving...</> : <><FontAwesomeIcon icon={faSave} /> Save</>}
            </Button>
          </div>
        </Form>
      </section>

      <div className="saved-env-var-sets-settings__list">
        {loading ? (
          <AccountSettingsLoadingState
            className="saved-env-var-sets-settings__loading"
            label="Loading saved variables"
            text="Loading saved variables..."
          />
        ) : items.length === 0 ? (
          <div className="saved-env-var-sets-settings__empty">
            No saved variables yet.
          </div>
        ) : (
          items.map((item) => {
            const keyNames = item.keyNames || [];
            return (
              <article key={item.id} className="saved-env-var-sets-settings__item-row">
                <div className="saved-env-var-sets-settings__item">
                  <div className="saved-env-var-sets-settings__item-main">
                    <div className="saved-env-var-sets-settings__item-name">{item.displayName}</div>
                    <div className="saved-env-var-sets-settings__item-key-summary">
                      {variableLabel(keyNames)}
                    </div>
                    <div className="saved-env-var-sets-settings__item-keys" aria-label={`Saved variables in ${item.displayName}`}>
                      {keyNames.length > 0 ? (
                        keyNames.map((keyName) => (
                          <span key={keyName} className="saved-env-var-sets-settings__item-key-name">
                            {keyName}
                          </span>
                        ))
                      ) : (
                        <span className="saved-env-var-sets-settings__item-no-keys">No variable names listed</span>
                      )}
                    </div>
                  </div>
                  <div className="saved-env-var-sets-settings__item-actions">
                    <Button variant="outline" size="sm" type="button" onClick={() => beginEdit(item)}>
                      Update
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      type="button"
                      disabled={deletingId === item.id}
                      onClick={() => handleDelete(item)}
                    >
                      {deletingId === item.id ? <BootstrapSpinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faTrash} />}
                    </Button>
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>

      <ConfirmationModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete saved variables"
        message={pendingDelete ? `Delete "${pendingDelete.displayName}"? These variables will no longer be available for new runs.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isProcessing={Boolean(deletingId)}
        children={null}
      />
    </div>
  );
};

export default SavedEnvVarSets;
